import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import { useParams, useNavigate, useOutletContext } from 'react-router-dom'
import type { Project, GccCombinableScenario, GccStage4Result } from '@shared/types'
import { combinedDashboardLabel, isCombinedStale } from '@shared/gcc/combine'
import { useApi, useGenerationContext } from '../index'
import GccExportMenu from './GccExportMenu'
import DispositionTab from './GccDispositionTab'
import RolesTab, { fmtUSD } from './GccRolesTab'
import { filterExcludedTaggedTasks } from './s0bExclusions'
import { countExcludedEliminate } from './dispositionTooltip'
import { gccTnFailure } from './gccTnStatus'
import GccTnAnalysisModal from './GccTnAnalysisModal'
import GccCombineModal from './GccCombineModal'
import GccTracePanel from './GccTracePanel'
import { gccTraceCalculations } from './gccTraceCalculations'
import GccSourcingModel, { Badge, fmtFte2 } from './GccSourcingModel'
import { BRAND_GRADIENT, GCC_BADGE, GCC_SCENARIO_BADGE, gccExecutiveVars, gccMotionAllowed, useCountUp } from './gccExecutiveStyle'
import GccSequencing from './GccSequencing'
import GccValueCase from './GccValueCase'
import GccRenameModal from './GccRenameModal'
import { useGccRename } from './useGccRename'
import { canRenameGccProject, gccDashboardDocumentTitle, gccDisplayName } from './gccDesignName'
import { resolveSessionIdentity } from '../utils/sessionIdentity'
import { GccTooltip } from './GccTooltip'
import { GCC_TOOLTIPS } from './gcc-tooltips'

type Tab = 'disposition' | 'sourcing' | 'sequencing' | 'valuecase' | 'roles'

function fmtDelta(n: number): string {
  return n > 0 ? `+${n}` : String(n)
}


// ── WVC Card ────────────────────────────────────────────────────────────────

interface WvcCardProps {
  title: string
  seg: GccStage4Result
  /** The card's left rule: a categorical colour of the export (vc-card-gcc / -ret / -comb). */
  rule: string
}

function WvcCard({ title, seg, rule }: WvcCardProps) {
  // Ledger headcounts are FTE to 0.01 (WI 2326): round so the delta shows no float noise
  const delta = Math.round((seg.totalHeadcountEndState - seg.totalHeadcountToday) * 100) / 100
  return (
    <div className="gx-card gx-lift gx-vc-card" style={{ '--vc': rule } as CSSProperties}>
      <p className="gx-vc-name">{title}</p>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <div className="gx-big">{seg.totalHeadcountEndState}</div>
          <GccTooltip content={GCC_TOOLTIPS.endStateHeadcount}>
            <span className="gx-cap">End-state headcount</span>
          </GccTooltip>
          <div className="text-xs font-semibold mt-1.5 text-gray-700">
            <GccTooltip content={GCC_TOOLTIPS.headcountDelta}>
              <span>{fmtDelta(delta)} from today</span>
            </GccTooltip>
          </div>
        </div>
        <div>
          <div className="gx-big gx-t-good">{fmtUSD(seg.labourSavingUSD)}</div>
          <GccTooltip content={GCC_TOOLTIPS.labourSaving}>
            <span className="gx-cap">Labour saving</span>
          </GccTooltip>
          <div className="gx-cap">vs. today's cost</div>
        </div>
      </div>
      {/* v2 (WI 2326): the saving split by value driver; absent on older designs */}
      {seg.aiSavingUSD !== undefined && seg.locationSavingUSD !== undefined && (
        <div className="text-xs" aria-label={`${title} saving split`}>
          <GccTooltip content={GCC_TOOLTIPS.savingSplit}>
            <span>
              <span className="font-semibold gx-t-ai">AI {fmtUSD(seg.aiSavingUSD)}</span>
              {' · '}
              <span className="font-semibold gx-t-loc">Location {fmtUSD(seg.locationSavingUSD)}</span>
            </span>
          </GccTooltip>
        </div>
      )}
      <div className="gx-vc-row">
        <div>
          <GccTooltip content={GCC_TOOLTIPS.labourCostToday}>
            <span className="gx-cap">Today </span>
          </GccTooltip>
          <span className="font-semibold">{fmtUSD(seg.totalLabourCostTodayUSD)}</span>
        </div>
        <div>
          <GccTooltip content={GCC_TOOLTIPS.labourCostEndState}>
            <span className="gx-cap">End-state </span>
          </GccTooltip>
          <span className="font-semibold">{fmtUSD(seg.totalLabourCostEndStateUSD)}</span>
        </div>
      </div>
    </div>
  )
}

/** A hero figure that counts up when motion is allowed. */
function HeroKpi({ value, format, label, motion }: { value: number; format: (n: number) => string; label: string; motion: boolean }) {
  const shown = useCountUp(value, motion)
  return (
    <div className="gx-kpi">
      <div className="gx-kpi-v">{format(shown)}</div>
      <div className="gx-kpi-l">{label}</div>
    </div>
  )
}

/**
 * Tooltip for a failed linked TN card: where the failure message came from and the IDs
 * needed to look it up in the server logs (WI 2417). The job ID saved on the project is
 * used when the failed job is not tracked in this session, e.g. after a refresh (WI 2420).
 */
export function tnFailureTooltip(
  failure: string,
  p: Pick<Project, 'id' | 'gccScenario' | 'gccTnError' | 'gccTnFailedAt' | 'gccTnJobId'>,
  failedJobId?: string,
): string {
  const jobId = failedJobId ?? p.gccTnJobId
  const source = p.gccTnError
    ? `Source: saved on this analysis by the server when its job failed${p.gccTnFailedAt ? ` (${p.gccTnFailedAt})` : ''}.`
    : failedJobId
      ? `Source: reported by the server for job ${failedJobId}.`
      : jobId
        ? `Source: job ${jobId} left no dashboard and no recorded failure, so the run was lost (for example a server restart).`
        : 'Source: no dashboard and no job were found for this analysis, so the run was lost (for example a server restart).'
  return [
    failure,
    '',
    source,
    `Analysis ID: ${p.id}`,
    ...(jobId ? [`Job ID: ${jobId}`] : []),
    ...(p.gccScenario ? [
      `Seed document: gcc-retained-workforce-${p.gccScenario}.txt, built by the server. The .json in the Run TN Analysis wizard is only a preview download and is not sent.`,
    ] : []),
  ].join('\n')
}

// ── Main Dashboard ───────────────────────────────────────────────────────────

// Sidebar links switch designs on the same route, which would reuse one instance and
// carry the previous design's state (TN cards, filters, modal) into the next (WI 2348).
// Keying by projectId gives each design a fresh instance and orphans in-flight fetches.
export default function GccDashboard() {
  const { projectId } = useParams<{ projectId: string }>()
  return <GccDashboardView key={projectId} />
}

function GccDashboardView() {
  const { projectId } = useParams<{ projectId: string }>()
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<Tab>('disposition')
  // ── Tab filter state (ephemeral — reset on tab switch) ──────────────────────
  const [showTnModal, setShowTnModal] = useState(false)
  const [showTrace, setShowTrace] = useState(false)
  const [tnStartedProjectId, setTnStartedProjectId] = useState<string | null>(null)
  const [tnProjects, setTnProjects] = useState<Project[]>([])
  const [sessionEmail, setSessionEmail] = useState('')
  /** Open Combine modal; `rebuild` preselects the scenario of an out-of-date combined dashboard. */
  const [combineModal, setCombineModal] = useState<{ rebuild?: GccCombinableScenario } | null>(null)
  /** Bumped after a rebuild, which keeps the same linked ID, so the cards still refetch. */
  const [tnRefreshKey, setTnRefreshKey] = useState(0)
  /** Read once: motion runs only when the browser allows it and reduced motion is off (WI 2487). */
  const [motion] = useState(gccMotionAllowed)
  /** Sliding indicator under the active tab; placed from the active button's box. */
  const tabIndicator = useRef<HTMLSpanElement>(null)
  const navigate = useNavigate()
  const api = useApi()
  const { trackJob, activeJobs, synced, generationCompletedAt } = useGenerationContext()
  // Absent when rendered outside GccLayout (e.g. tests) — the sidebar refresh is then a no-op.
  const layout = useOutletContext<{ refreshProjects?: () => Promise<void> } | undefined>()
  const rename = useGccRename(async (updated) => {
    setProject(updated)
    await layout?.refreshProjects?.()
  })

  const deleteTnProject = async (tnProjectId: string) => {
    if (!project || !projectId) return
    const isRunning = activeJobs.some(j => j.projectId === tnProjectId && (j.status === 'pending' || j.status === 'running'))
    if (isRunning) { alert('This analysis is still running. Wait for it to finish before deleting.'); return }
    const target = tnProjects.find(p => p.id === tnProjectId)
    // A combined dashboard is only valid while both of its sources exist (WI 1999).
    const dependents = target?.gccCombined ? [] : tnProjects.filter(p =>
      p.gccCombined && (p.gccCombined.retainedProjectId === tnProjectId || p.gccCombined.offshoreProjectId === tnProjectId))
    const message = target?.gccCombined
      ? 'Delete this combined dashboard? The two source analyses are kept.'
      : dependents.length > 0
        ? `Delete this TN Analysis? This cannot be undone.\n\nThis will also remove ${dependents.map(d => d.gccCombined ? combinedDashboardLabel(d.gccCombined.offshoreScenario) : d.name).join(' and ')}, which is built from it.`
        : 'Delete this TN Analysis? This cannot be undone.'
    if (!window.confirm(message)) return
    const removeIds = [tnProjectId, ...dependents.map(d => d.id)]
    try {
      await api.deleteProject(tnProjectId)
      await Promise.all(dependents.map(d => api.deleteProject(d.id).catch(() => {})))
      const fresh = await api.getProject(projectId)
      if (!fresh) return
      const updatedIds = (fresh.derivedTnProjectIds ?? []).filter(id => !removeIds.includes(id))
      await api.saveProject({ ...fresh, derivedTnProjectIds: updatedIds, updatedAt: new Date().toISOString() } as Parameters<typeof api.saveProject>[0])
      setProject({ ...fresh, derivedTnProjectIds: updatedIds })
      setTnProjects(prev => prev.filter(p => !removeIds.includes(p.id)))
      if (tnStartedProjectId && removeIds.includes(tnStartedProjectId)) setTnStartedProjectId(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete TN Analysis.')
    }
  }

  useEffect(() => {
    if (!projectId) return
    void api.getProject(projectId)
      .then(p => setProject(p))
      .catch(() => setProject(null))
      .finally(() => setLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api is stable from context
  }, [projectId])

  // Load all linked TN projects for the cards section. Refetch whenever a tracked job
  // finishes (generationCompletedAt) so a card flips Generating… → Ready or Failed,
  // without the user leaving the page. A failed run keeps its project (WI 2399).
  useEffect(() => {
    const ids = project?.derivedTnProjectIds ?? []
    if (ids.length === 0) { setTnProjects([]); return }
    let cancelled = false
    void Promise.all(ids.map(id => api.getProject(id).catch(() => null)))
      .then(results => {
        if (!cancelled) setTnProjects(results.filter((p): p is Project => p !== null))
      })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api is stable from context
  }, [project?.derivedTnProjectIds?.join(','), generationCompletedAt, tnRefreshKey])

  useEffect(() => {
    void resolveSessionIdentity(api).then(({ email }) => setSessionEmail(email))
  }, [api])

  // GccLayout owns the generic tab title and restores it; this only overrides it while a
  // design is shown, and re-runs after a rename so the tab follows the new name.
  const documentTitle = project ? gccDashboardDocumentTitle(project) : null
  useEffect(() => {
    if (!documentTitle) return
    const prev = document.title
    document.title = documentTitle
    return () => { document.title = prev }
  }, [documentTitle])

  // Slide the indicator under the active tab, and keep it there when the tab bar resizes.
  const hasDesign = !!project?.gccData
  useLayoutEffect(() => {
    const indicator = tabIndicator.current
    if (!indicator) return
    const place = () => {
      // One tab is always pressed, and the indicator sits in the same group as the tabs
      const active = (indicator.parentElement as HTMLElement).querySelector('[aria-pressed="true"]') as HTMLElement
      indicator.style.left = `${active.offsetLeft}px`
      indicator.style.width = `${active.offsetWidth}px`
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [tab, hasDesign, loading])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        Loading GCC design…
      </div>
    )
  }

  if (!project?.gccData) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-8">
        <div className="text-5xl">📊</div>
        <h2 className="text-xl font-semibold text-gray-800">GCC design not ready</h2>
        <p className="text-sm text-gray-500 max-w-sm">
          The GCC pipeline may still be running. Return to the home screen to monitor progress.
        </p>
        <button onClick={() => navigate('/gcc')} className="mt-2 text-sm text-purple-600 hover:text-purple-700 font-medium underline">
          ← Back to home
        </button>
      </div>
    )
  }

  const gcc = project.gccData
  const title = gccDisplayName(project)
  const canRename = canRenameGccProject(api.platform, sessionEmail, project)
  const reviewPending = !gcc.s0bReview?.proceededAt
  const hasTrace = (gcc.apiTrace?.length ?? 0) > 0
  const hasTnCapability = typeof api.runTnOnRetainedWorkforce === 'function'

  const isTnActive = (id: string) => activeJobs.some(j => j.projectId === id && (j.status === 'pending' || j.status === 'running'))
  const isTnReady = (p: Project | undefined) => !!p?.data && !isTnActive(p.id)
  /** Why a linked analysis failed, or null when it is ready, running or not yet known. */
  const tnFailure = (p: Project): string | null => gccTnFailure(p, activeJobs, synced)
  const tnAnalyses = tnProjects.filter(p => !p.gccCombined)
  const combinedProjects = tnProjects.filter(p => !!p.gccCombined)
  const retainedTn = tnAnalyses.find(p => p.gccScenario === 'Retained')
  const combinableTn: Partial<Record<GccCombinableScenario, Project>> = {
    Medium: tnAnalyses.find(p => p.gccScenario === 'Medium'),
    Low: tnAnalyses.find(p => p.gccScenario === 'Low'),
  }
  const canCombine = typeof api.combineGccTnAnalyses === 'function'
    && isTnReady(retainedTn)
    && (isTnReady(combinableTn.Medium) || isTnReady(combinableTn.Low))

  const tabs: { key: Tab; label: string }[] = [
    { key: 'disposition', label: 'Disposition' },
    { key: 'sourcing', label: 'Sourcing Model' },
    { key: 'sequencing', label: 'Sequencing' },
    // Designs saved before WI 2326 have no value ledger, so they keep the original tabs
    ...(gcc.valueCase ? [{ key: 'valuecase' as const, label: 'Value Case' }] : []),
    { key: 'roles', label: 'End-State Roles' },
  ]

  return (
    <div className={`gcc-exec flex flex-col h-full${motion ? ' gccx-motion' : ''}`} style={gccExecutiveVars(project.branding)}>
      {/* Toolbar */}
      <div className="gx-toolbar flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate('/gcc')} className="gx-crumb flex-shrink-0">
            ← Home
          </button>
          <span className="gx-sep flex-shrink-0">/</span>
          <span className="gx-dot flex-shrink-0" />
          <span className="font-semibold truncate">{title}</span>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {hasTnCapability && (
            <button onClick={() => setShowTnModal(true)} className="gx-btn-dark">
              Run TN Analysis
            </button>
          )}
          <GccExportMenu gcc={gcc} project={project} tnProjects={tnProjects} />
          <span className="gx-badge-dark">TN GLOBAL DESIGN</span>
        </div>
      </div>

      {/* Scrollable content: hero, tab bar joined to it, then the page body.
          Positioned so sr-only and other absolute children scroll and clip here; unpositioned,
          they belonged to the window and gave the page its own scrollbar (WI 2754). */}
      <div className="relative flex-1 overflow-y-auto">
        <section className="gx-hero" aria-label="Design summary">
          <div className="gx-hero-grid" />
          <div className="gx-hero-top">
            <div className="gx-wordmark"><b>Global Capability Center</b><span>Design Engine</span></div>
            {project.industry && (
              <div className="gx-chips">
                <div className="gx-chip"><small>Industry</small><strong>{project.industry}</strong></div>
              </div>
            )}
          </div>
          <div className="gx-eyebrow">GCC design</div>
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="gx-hero-title">{title}</h1>
            {canRename && (
              <button
                onClick={() => rename.open(project)}
                title="Rename design"
                aria-label="Rename design"
                className="gx-rename"
              >
                ✎
              </button>
            )}
          </div>
          <div className="gx-kpis" aria-label="Headline figures">
            <HeroKpi value={gcc.gccPopulation.totalHeadcountEndState} format={fmtFte2} label="GCC-Eligible" motion={motion} />
            <HeroKpi value={gcc.retainedPopulation.totalHeadcountEndState} format={fmtFte2} label="Retained" motion={motion} />
            <HeroKpi value={gcc.combined.labourSavingUSD} format={fmtUSD} label="Combined Saving" motion={motion} />
          </div>

          {/* WVC Cards */}
          <section className="gx-sec gx-rv" aria-label="Workforce Value Case">
            <div className="gx-sec-head"><h2 className="gx-sec-title">Workforce Value Case</h2></div>
            <div className="gx-vc-cards">
              <WvcCard title="GCC-Eligible Population"   seg={gcc.gccPopulation}      rule="var(--c-gcc)" />
              <WvcCard title="Retained Population"        seg={gcc.retainedPopulation} rule="var(--c-ret)" />
              <WvcCard title="Combined (Full Enterprise)" seg={gcc.combined}            rule="linear-gradient(180deg, var(--brand), var(--c-gcc))" />
            </div>
          </section>

          {/* Linked TN Analyses cards */}
          {(tnProjects.length > 0 || tnStartedProjectId) && (
            <section className="gx-sec gx-rv" aria-label="Linked TN Analyses" style={{ '--d': '.08s' } as CSSProperties}>
              <div className="gx-sec-head">
                <h2 className="gx-sec-title">Linked TN Analyses</h2>
                {canCombine && (
                  <button
                    onClick={() => setCombineModal({})}
                    title="Roll the Retained (HQ + Markets) analysis up with the GCC-eligible residual of Medium or Low"
                    className="gx-btn"
                  >
                    Combine →
                  </button>
                )}
              </div>
              <div className="gx-tn-cards">
                {tnAnalyses.map((tnp) => {
                  const isActive = isTnActive(tnp.id)
                  const hasDashboard = !!tnp.data
                  const failure = tnFailure(tnp)
                  const scenario = tnp.gccScenario
                  const scenarioStyle = (scenario && GCC_SCENARIO_BADGE[scenario]) || GCC_BADGE.gray
                  return (
                    <div
                      key={tnp.id}
                      role="button"
                      tabIndex={0}
                      className={`gx-card gx-lift gx-tn-card group${failure ? ' is-failed' : ''}`}
                      onClick={() => navigate(`/gcc/tn-dashboard/${tnp.id}`)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/gcc/tn-dashboard/${tnp.id}`) }}
                    >
                      <div className="gx-strip" style={{ '--st': scenarioStyle.text } as CSSProperties} />
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          {scenario && <Badge label={scenario} style={scenarioStyle} />}
                          <span className="text-xs font-semibold text-gray-700">
                            TN Analysis ({scenario === 'Retained' ? 'HQ + Markets' : scenario ?? '—'})
                          </span>
                          <span className="ml-auto text-gray-400 text-xs">→</span>
                        </div>
                        <p className="text-xs text-gray-500 truncate">{tnp.name || tnp.client || 'Retained Workforce'}</p>
                        {failure && (
                          <p
                            className="mt-1 text-[11px] text-red-700 line-clamp-2 cursor-help"
                            title={tnFailureTooltip(failure, tnp, activeJobs.find(j => j.projectId === tnp.id && j.status === 'failed')?.jobId)}
                          >
                            {failure}
                          </p>
                        )}
                        <div className="mt-2 flex items-center justify-between">
                          <div>
                            {isActive ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                Generating…
                              </span>
                            ) : failure ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                Failed
                              </span>
                            ) : hasDashboard ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                Ready
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full">
                                Pending
                              </span>
                            )}
                          </div>
                          <button
                            onClick={e => { e.stopPropagation(); void deleteTnProject(tnp.id) }}
                            disabled={isActive}
                            className={`${failure ? 'text-red-600' : 'opacity-0 group-hover:opacity-100 text-gray-400'} focus:opacity-100 hover:text-red-600 text-base leading-none transition-opacity px-1 disabled:cursor-not-allowed`}
                            title={isActive ? 'Cannot delete while generating' : failure ? 'Delete failed TN Analysis' : 'Delete TN Analysis'}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
                {combinedProjects.map((cp) => {
                  const source = cp.gccCombined
                  if (!source) return null
                  const stale = isCombinedStale(
                    source,
                    tnAnalyses.find(p => p.id === source.retainedProjectId),
                    tnAnalyses.find(p => p.id === source.offshoreProjectId),
                  )
                  const canRebuild = stale && typeof api.combineGccTnAnalyses === 'function'
                    && isTnReady(retainedTn) && isTnReady(combinableTn[source.offshoreScenario])
                  return (
                    <div
                      key={cp.id}
                      role="button"
                      tabIndex={0}
                      className="gx-card gx-lift gx-tn-card group"
                      onClick={() => navigate(`/gcc/tn-dashboard/${cp.id}`)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/gcc/tn-dashboard/${cp.id}`) }}
                    >
                      <div className="gx-strip" style={{ '--st': BRAND_GRADIENT } as CSSProperties} />
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="gx-pill-brand">Combined</span>
                          <span className="text-xs font-semibold text-gray-700">{combinedDashboardLabel(source.offshoreScenario)}</span>
                          <span className="ml-auto text-gray-400 text-xs">→</span>
                        </div>
                        <p className="text-xs text-gray-500">HQ + Markets from Retained · GCC-eligible residual from {source.offshoreScenario}</p>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            {/* The title row already carries the Combined pill, so a fresh card shows no status here (WI 2580) */}
                            {stale && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" title="A source analysis changed after this dashboard was built">
                                Out of date
                              </span>
                            )}
                            {canRebuild && (
                              <button
                                onClick={e => { e.stopPropagation(); setCombineModal({ rebuild: source.offshoreScenario }) }}
                                className="gx-link font-semibold"
                              >
                                Rebuild
                              </button>
                            )}
                          </div>
                          <button
                            onClick={e => { e.stopPropagation(); void deleteTnProject(cp.id) }}
                            className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-red-600 text-base leading-none transition-opacity px-1"
                            title="Delete combined dashboard"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
                {/* Newly started TN not yet in derivedTnProjectIds */}
                {tnStartedProjectId && !tnProjects.some(p => p.id === tnStartedProjectId) && (
                  <button
                    onClick={() => navigate(`/gcc/tn-dashboard/${tnStartedProjectId}`)}
                    className="gx-card gx-lift gx-tn-card"
                  >
                    <div className="gx-strip" style={{ '--st': '#F59E0B' } as CSSProperties} />
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-gray-700">TN Analysis (starting)</span>
                        <span className="ml-auto text-gray-400 text-xs">→</span>
                      </div>
                      <p className="text-xs text-gray-500">Retained Workforce</p>
                      <div className="mt-2">
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                          Generating…
                        </span>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            </section>
          )}
        </section>

        <nav className="gx-tabnav" aria-label="Dashboard tabs">
          <div role="group" aria-label="Dashboard sections" className="gx-tabnav-inner">
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                aria-pressed={tab === t.key}
                className="gx-tab"
              >
                {t.label}
              </button>
            ))}
            <span ref={tabIndicator} className="gx-tab-ind" aria-hidden="true" />
          </div>
        </nav>

        <div className="gx-body">
          {/* S0b review banner */}
          {reviewPending && (
            <div
              role="region"
              aria-label="Task Register review pending"
              className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-5 py-3 flex items-center justify-between gap-4"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-amber-500 text-lg flex-shrink-0">⚑</span>
                <div>
                  <p className="text-xs font-semibold text-amber-800">Task Register Review Pending</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Verify the AI-generated task register before proceeding to the sourcing analysis.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/gcc/review/${projectId}`)}
                className="flex-shrink-0 text-xs font-semibold text-amber-700 hover:text-amber-900 border border-amber-300 rounded-lg px-3 py-1.5 hover:bg-amber-100 transition-colors whitespace-nowrap"
              >
                Review now →
              </button>
            </div>
          )}

          {/* Active tab; keyed so its content rises in again on every switch */}
          <section key={tab} className="gx-sec gx-card gx-panel gx-rv" aria-label={`${tabs.find(t => t.key === tab)?.label} tab`} style={{ '--d': '.12s' } as CSSProperties}>
            {tab === 'disposition' && (
              gcc.taggedTasks?.taggedTasks ? (
                <DispositionTab taggedTasks={filterExcludedTaggedTasks(gcc)} residual={gcc.taggedTasks.residualWorkload} taskSourcing={gcc.sourcingModel?.taskSourcing} excludedEliminate={countExcludedEliminate(gcc)} quickWinHorizonMonths={gcc.taggedTasks.quickWinHorizonMonths} />
              ) : (
                <p className="text-sm text-gray-400">Disposition data unavailable.</p>
              )
            )}

            {tab === 'sourcing' && <GccSourcingModel stage2={gcc.sourcingModel} />}

            {tab === 'sequencing' && <GccSequencing stage3={gcc.sequencing} quickWinHorizonMonths={gcc.taggedTasks?.quickWinHorizonMonths} />}

            {tab === 'valuecase' && <GccValueCase valueCase={gcc.valueCase} />}

            {tab === 'roles' && <RolesTab gcc={gcc} />}
          </section>
        </div>
      </div>

      {/* Rename modal */}
      {rename.target && <GccRenameModal {...rename} />}

      {/* Pipeline traceability FAB: palette colours from the container (gx-trace-fab) */}
      {hasTrace && !showTrace && (
        <button
          onClick={() => setShowTrace(true)}
          className="gx-trace-fab"
          title="Pipeline Trace"
          aria-label="Open pipeline trace"
        >
          ⚙
        </button>
      )}

      {/* Trace slide-out panel */}
      {showTrace && hasTrace && (
        <GccTracePanel trace={gcc.apiTrace ?? []} calculations={gccTraceCalculations(gcc)} onClose={() => setShowTrace(false)} />
      )}

      {/* TN Analysis modal */}
      {showTnModal && (
        <GccTnAnalysisModal
          projectId={projectId ?? ''}
          gcc={gcc}
          clientName={project?.client}
          industry={project?.industry}
          usedScenarios={tnAnalyses.map(p => p.gccScenario).filter((s): s is NonNullable<typeof s> => s != null)}
          failedScenarios={Object.fromEntries(tnAnalyses.flatMap(p => {
            const failure = tnFailure(p)
            return p.gccScenario && failure ? [[p.gccScenario, failure]] : []
          }))}
          onDeleteScenario={async (scenario) => {
            const target = tnAnalyses.find(p => p.gccScenario === scenario)
            if (target) await deleteTnProject(target.id)
          }}
          onClose={() => setShowTnModal(false)}
          runTn={api.runTnOnRetainedWorkforce ? async ({ gccProjectId, scenario }) => {
            const settings = await api.getSettings()
            const apiKey = settings.aiProvider === 'claude' ? settings.anthropicApiKey
              : settings.aiProvider === 'openai' ? settings.openaiApiKey
              : settings.azureApiKey
            const branding = project?.branding ?? {}
            if (!api.runTnOnRetainedWorkforce) throw new Error('runTnOnRetainedWorkforce is not supported by this API client')
            return api.runTnOnRetainedWorkforce({ gccProjectId, scenario, provider: settings.aiProvider, apiKey, branding })
          } : undefined}
          onTnStarted={({ jobId, projectId: tnProjectId }) => {
            setTnStartedProjectId(tnProjectId)
            trackJob(jobId, tnProjectId, 'inside-out', project?.client ?? 'TN Analysis', '#A100FF')
            if (projectId) void api.getProject(projectId).then(fresh => { if (fresh) setProject(fresh) })
          }}
        />
      )}

      {/* Combine (roll-up) modal */}
      {combineModal && (
        <GccCombineModal
          gcc={gcc}
          retained={retainedTn}
          scenarioProjects={combinableTn}
          isReady={isTnReady}
          existing={combinedProjects.map(p => p.gccCombined?.offshoreScenario).filter((s): s is GccCombinableScenario => s != null)}
          rebuildScenario={combineModal.rebuild}
          combine={async (offshoreScenario) => {
            if (!api.combineGccTnAnalyses) throw new Error('Combining analyses is not supported by this API client')
            return api.combineGccTnAnalyses({ gccProjectId: projectId ?? '', offshoreScenario })
          }}
          onCombined={() => {
            setTnRefreshKey(k => k + 1)
            if (projectId) void api.getProject(projectId).then(fresh => { if (fresh) setProject(fresh) })
          }}
          onClose={() => setCombineModal(null)}
        />
      )}
    </div>
  )
}
